import { useApolloClient } from '@apollo/client';
import {
  BKJButton,
  BKJCheckbox,
  BKJLoadingSpinner,
  BKJSelectSearch,
} from '@bookjane2/bookjane-design-library';
import { Flex } from 'components/Flex';
import { H3, P } from 'components/Typography';
import { useTranslation } from 'hooks/useTranslation';
import { getCommunitiesSkillsAttributes } from 'pages/Settings/pages/LocationSettings/SkillsAllocation/SkillsAllocationSettingsPage.constants';
import {
  AppliedLabel,
  ApplyToLocationCell,
  EmptyStateMessage,
  SkillsAllocationTableRow,
} from 'pages/Settings/pages/LocationSettings/SkillsAllocation/SkillsAllocationSettingsPage.styled';
import { useLocationSkillsStore } from 'pages/Settings/pages/LocationSettings/SkillsAllocation/store/locationSkills.store';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

export const SkillsAllocationEditView: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const apolloClient = useApolloClient();
  const { locationId } = useParams<{ locationId: string }>();
  const [searchTerm, setSearchTerm] = useState('');

  const organizationSkills = useLocationSkillsStore((state) => state.organizationSkills);
  const appliedCommunitiesSkills = useLocationSkillsStore(
    (state) => state.appliedCommunitiesSkills,
  );
  const selectedSkillIds = useLocationSkillsStore((state) => state.selectedSkillIds);
  const isLoading = useLocationSkillsStore((state) => state.isLoading);
  const isSaving = useLocationSkillsStore((state) => state.isSaving);
  const toggleSkill = useLocationSkillsStore((state) => state.toggleSkill);
  const discardChanges = useLocationSkillsStore((state) => state.discardChanges);
  const saveSkills = useLocationSkillsStore((state) => state.saveSkills);

  const basePath = `/settings/locations/${locationId}/skills`;

  useEffect(() => discardChanges, [discardChanges]);

  const visibleSkills = useMemo(
    () =>
      organizationSkills.filter((skill) =>
        skill.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
      ),
    [organizationSkills, searchTerm],
  );

  const hasChanges = useMemo(
    () => getCommunitiesSkillsAttributes(appliedCommunitiesSkills, selectedSkillIds).length > 0,
    [appliedCommunitiesSkills, selectedSkillIds],
  );

  const handleSave = async () => {
    if (await saveSkills(apolloClient)) history.push(basePath);
  };

  return (
    <Flex flexDirection="column" width="100%" maxWidth="844px">
      <Flex minHeight="44px" margin="0 0 24px 0">
        <H3>{t('settings:SKILLS')}</H3>
      </Flex>
      <Flex flexDirection="column" gap="32px">
        <Flex maxWidth="672px">
          <P>{t('location_settings:LOCATION_SKILLS_PAGE_NOTE')}</P>
        </Flex>
        <Flex maxWidth="496px">
          <BKJSelectSearch
            name="searchTerm"
            value={searchTerm}
            placeholder={t('settings:SEARCH_SKILLS')}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
          />
        </Flex>
        {isLoading && (
          <Flex justifyContent="center" padding="40px 0">
            <BKJLoadingSpinner variant="Default" />
          </Flex>
        )}
        {!isLoading && !!visibleSkills.length && (
          <Flex flexDirection="column" width="100%">
            <SkillsAllocationTableRow $isHeader $hasApplyColumn>
              {t('settings:SKILL')}
              <Flex justifyContent="center">{t('location_settings:APPLY_TO_LOCATION')}</Flex>
            </SkillsAllocationTableRow>
            {visibleSkills.map((skill) => (
              <SkillsAllocationTableRow key={skill.id} $hasApplyColumn>
                {skill.name}
                <ApplyToLocationCell>
                  <BKJCheckbox
                    variant="GreenSolid"
                    name={skill.id}
                    value={skill.applyToAllLocations || selectedSkillIds.includes(skill.displayId)}
                    disabled={skill.applyToAllLocations}
                    onChange={() => toggleSkill(skill.displayId)}
                  />
                  {skill.applyToAllLocations && (
                    <AppliedLabel>{t('location_settings:APPLIED')}</AppliedLabel>
                  )}
                </ApplyToLocationCell>
              </SkillsAllocationTableRow>
            ))}
          </Flex>
        )}
        {!isLoading && !organizationSkills.length && (
          <EmptyStateMessage>
            {t('location_settings:NO_ORGANIZATION_SKILLS_MESSAGE')}
          </EmptyStateMessage>
        )}
        {!isLoading && !!organizationSkills.length && !visibleSkills.length && (
          <EmptyStateMessage>{t('settings:NO_SEARCHED_SKILL_RESULTS')}</EmptyStateMessage>
        )}
      </Flex>
      <Flex gap="16px" flexWrap="wrap" margin="40px 0 0 0">
        <BKJButton
          variant="PurpleSolid"
          width="192px"
          minWidth="192px"
          isLoading={isSaving}
          disabled={!hasChanges || isSaving}
          onClick={handleSave}
        >
          {t('auth:SAVE')}
        </BKJButton>
        <BKJButton
          variant="PurpleOutlined"
          width="192px"
          minWidth="192px"
          onClick={() => history.push(basePath)}
        >
          {t('auth:CANCEL')}
        </BKJButton>
      </Flex>
    </Flex>
  );
};
